// ============================================================
// THE DAEMONIC TREATMENT, DRAWN (plan body-map T1; engine/plate/daemonic).
// Its filters (the crawl over the figure, the echo's colour, the corona's
// wisps); under the figure, outside it (the echo copies its still lines),
// the corona, the echo and the shadow falling toward no light; at death the
// figure banished - collapsing to a point - with a flash. The filters' noise
// re-forms on tracks (the bench's SMIL), the echo drifts on its `<use>`'s
// x and y. Its colours are tokens, flooded onto the figure's own shape.
// ============================================================

import type { CSSProperties } from 'react';
import { daemonLook } from '../../../../engine/plate/daemonic';
import type { Box } from '../../../../engine/plate/frame';
import tx from '../../../../styles/PlateTraits.module.css';
import { tracked } from '../../usePlateClock';

const ECHO = { floodColor: 'var(--plate-daemon-echo)', floodOpacity: 0.9 } as CSSProperties;
const CORONA = { floodColor: 'var(--plate-daemon-corona)' } as CSSProperties;

/** Its filters at a strength: the crawl over the figure, the echo's colour, the corona's rising wisps. */
export function DaemonDefs({ u, power }: { u: string; power: number }) {
  const { crawl, corona } = daemonLook(power);
  return (
    <>
      <filter id={`${u}-wrong`} x="-10%" y="-10%" width="120%" height="120%">
        <feTurbulence type="fractalNoise" numOctaves={2} result="n" {...tracked([crawl.seed, crawl.frequency])} />
        <feDisplacementMap in="SourceGraphic" in2="n" scale={crawl.scale} xChannelSelector="R" yChannelSelector="G" />
      </filter>
      <filter id={`${u}-echo`} x="-20%" y="-20%" width="140%" height="140%">
        <feFlood style={ECHO} />
        <feComposite in2="SourceGraphic" operator="in" />
      </filter>
      <filter id={`${u}-corona`} x="-30%" y="-30%" width="160%" height="160%">
        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.006 0.03"
          numOctaves={2}
          result="n"
          {...tracked([corona.seed])}
        />
        <feDisplacementMap
          in="SourceGraphic"
          in2="n"
          scale={corona.scale}
          xChannelSelector="R"
          yChannelSelector="G"
          result="d"
        />
        <feOffset in="d" dy={corona.rise} result="up" />
        <feGaussianBlur in="up" stdDeviation={corona.blur} result="b" />
        <feFlood style={CORONA} />
        <feComposite in2="b" operator="in" result="c" />
        <feMerge>
          <feMergeNode in="c" />
          <feMergeNode in="c" />
        </feMerge>
      </filter>
    </>
  );
}

interface UnderProps {
  u: string;
  power: number;
  box: Box;
  /** Banished: the corona and the echo fade with it. */
  dead: boolean;
}

/**
 * Under the figure, outside it: the corona off its outline, the echo (the whole figure again, in the warp's colour, a
 * little larger, drifting a beat behind) and its shadow, stretched along the ground toward a light that is not there.
 */
export function DaemonUnder({ u, power, box: [x0, , x1, y1], dead }: UnderProps) {
  const { corona, echo } = daemonLook(power);
  const [cx, cy] = [(x0 + x1) / 2, 1500];
  return (
    <g pointerEvents="none" data-trait="daemonic" data-gone={dead || undefined}>
      <g className={dead ? tx.auraGone : undefined}>
        <g
          className={tx.daemonShadow}
          transform={`translate(${cx} ${y1}) matrix(1 0 0.9 0.32 0 0) translate(${-cx} ${-y1})`}
        >
          <use href={`#${u}-sil`} />
        </g>
        <g opacity={corona.opacity} filter={`url(#${u}-corona)`}>
          <use href={`#${u}-art`} />
        </g>
        <g opacity={echo.opacity}>
          <g
            transform={`translate(${cx} ${cy}) scale(${echo.scale}) translate(${-cx} ${-cy})`}
            filter={`url(#${u}-echo)`}
          >
            <use href={`#${u}-art`} {...tracked([echo.x, echo.y])} />
          </g>
        </g>
      </g>
    </g>
  );
}

/** At its death, over the plate: the flash it is banished in. */
export function BanishFlash({ box: [x0, , x1] }: { box: Box }) {
  return <circle className={tx.banishFlash} cx={(x0 + x1) / 2} cy={1500} r={260} data-testid="plate-banished" />;
}
